import { useEffect, useRef, useState } from "react";
import {
  useNafathVerification,
  type NafathVerificationResult,
} from "./useNafathVerification";

const onlyDigits = (s: string) => (s || "").replace(/\D/g, "");

export type NafathVerificationStepProps = {
  /** Pre-filled national ID (signup flow). When set, hides the ID input. */
  nationalId?: string;
  /** Show national ID input when nationalId prop is empty (login test flow). */
  showNationalIdInput?: boolean;
  /** Start Nafath automatically when mounted (signup after OTP). */
  autoStart?: boolean;
  /** Show transId / status JSON — development only unless explicitly enabled. */
  showDebug?: boolean;
  /** «تحقق الآن» / «إيقاف الاستعلام» — development only unless explicitly enabled. */
  showAdvancedControls?: boolean;
  title?: string;
  subtitle?: string;
  onCompleted: (result: NafathVerificationResult) => void | Promise<void>;
  onBack?: () => void;
  disabled?: boolean;
};

export default function NafathVerificationStep({
  nationalId: nationalIdProp = "",
  showNationalIdInput,
  autoStart = false,
  showDebug = false,
  showAdvancedControls = process.env.NODE_ENV === "development",
  title = "التحقق عبر نفاذ",
  subtitle = "ستظهر لك أرقام التحقق لإكمال الموافقة في تطبيق نفاذ.",
  onCompleted,
  onBack,
  disabled = false,
}: NafathVerificationStepProps) {
  const [inputNationalId, setInputNationalId] = useState(nationalIdProp);
  const autoStartedRef = useRef(false);

  const resolvedNationalId = nationalIdProp.trim() || inputNationalId.trim();
  const showInput = showNationalIdInput ?? !nationalIdProp.trim();

  const nafath = useNafathVerification({
    nationalId: resolvedNationalId,
    onCompleted,
  });

  useEffect(() => {
    if (nationalIdProp.trim()) {
      setInputNationalId(nationalIdProp);
    }
  }, [nationalIdProp]);

  useEffect(() => {
    if (!autoStart || disabled || autoStartedRef.current) return;
    if (!resolvedNationalId || onlyDigits(resolvedNationalId).length < 6) return;
    autoStartedRef.current = true;
    void nafath.start();
  }, [autoStart, disabled, resolvedNationalId, nafath.start]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!disabled) void nafath.start();
  };

  const completed = !!nafath.identity;

  return (
    <section>
      <div className="mb-4 text-center">
        <h2 className="text-base font-semibold text-sa-700">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500 mt-2">{subtitle}</p>}
      </div>

      {!completed && (
        <>
          {showInput && (
            <form onSubmit={handleSubmit} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهوية</label>
              <input
                className="w-full h-11 rounded-xl border border-gray-300 px-3 text-left"
                dir="ltr"
                inputMode="numeric"
                value={inputNationalId}
                onChange={(e) => setInputNationalId(onlyDigits(e.target.value).slice(0, 12))}
                placeholder="مثال: 1001198892"
                disabled={disabled || nafath.busy || nafath.polling}
              />
            </form>
          )}

          {nafath.randomCode != null ? (
            <div className="rounded-xl bg-sa-50 border border-sa-200 px-4 py-3 text-center">
              <div className="text-sm text-gray-600">الرقم المعروض في تطبيق نفاذ</div>
              <div className="text-3xl font-bold text-sa-800 tracking-widest dir-ltr">{nafath.randomCode}</div>
              <div className="mt-2 text-xs text-gray-600">
                افتح تطبيق نفاذ وأدخل الرقم ثم وافق على الطلب.
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              {autoStart ? "جاري بدء نفاذ..." : "اضغط «بدء نفاذ» لإنشاء طلب جديد."}
            </div>
          )}

          {showDebug && (nafath.requestId || nafath.transId) && (
            <div className="mt-3 space-y-1 text-xs text-gray-500" dir="ltr">
              {nafath.requestId && <div>requestId: {nafath.requestId}</div>}
              {nafath.transId && <div>transId: {nafath.transId}</div>}
            </div>
          )}

          {showDebug && nafath.lastStatus && !completed && (
            <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm">
              <div className="font-semibold text-gray-800 mb-1">آخر حالة من الخادم</div>
              <pre className="text-xs overflow-auto whitespace-pre-wrap break-all" dir="ltr">
                {JSON.stringify(nafath.lastStatus, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}

      {completed && nafath.identity && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <div className="font-semibold mb-1">تم التحقق عبر نفاذ</div>
          <div>{nafath.identity.fullNameAr ?? nafath.identity.fullNameEn ?? "—"}</div>
        </div>
      )}

      {nafath.error && (
        <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {nafath.error}
        </div>
      )}
      {nafath.info && !nafath.error && (
        <div className="mt-4 rounded-xl border border-sa-200 bg-sa-50 px-3 py-2 text-sm text-sa-800" role="status">
          {nafath.info}
        </div>
      )}

      {!completed && (
        <div className="mt-5 flex flex-wrap gap-2 justify-end">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              disabled={disabled || nafath.busy}
              className="h-11 px-4 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              رجوع
            </button>
          )}
          {showAdvancedControls && nafath.polling && nafath.transId.trim() && (
            <button
              type="button"
              onClick={() => void nafath.checkNow()}
              disabled={disabled}
              className="h-11 px-4 rounded-xl border border-sa-300 text-sa-800 hover:bg-sa-50 disabled:opacity-50"
            >
              تحقق الآن
            </button>
          )}
          {showAdvancedControls && nafath.polling && (
            <button
              type="button"
              onClick={nafath.stopPolling}
              disabled={disabled}
              className="h-11 px-4 rounded-xl border border-amber-300 text-amber-900 hover:bg-amber-50 disabled:opacity-50"
            >
              إيقاف الاستعلام
            </button>
          )}
          {!autoStart && (
            <button
              type="button"
              onClick={() => void nafath.start()}
              disabled={disabled || nafath.busy || nafath.polling}
              className="h-11 px-5 rounded-xl bg-sa-600 text-white font-semibold hover:bg-sa-700 disabled:opacity-50"
            >
              {nafath.busy ? "جاري البدء..." : nafath.randomCode ? "إعادة المحاولة" : "بدء نفاذ"}
            </button>
          )}
          {autoStart && nafath.randomCode && !nafath.polling && (
            <button
              type="button"
              onClick={() => void nafath.start()}
              disabled={disabled || nafath.busy}
              className="h-11 px-5 rounded-xl bg-sa-600 text-white font-semibold hover:bg-sa-700 disabled:opacity-50"
            >
              إعادة المحاولة
            </button>
          )}
        </div>
      )}
    </section>
  );
}
